import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  isUserLoggedIn,
  selectCheckTariffLimitPerUserAccount,
  selectExhibitionSettingsLoginMode,
  selectIsAddTicketsAfterLogin
} from '@app/app.reducer';
import { LoginOptions } from '@app/login/login.model';
import {
  ChangeVisibilityType,
  ProductBookingLoginGuardState
} from '@core/data-access/models/booking/product-booking-login-guard.model';
import { Store } from '@ngrx/store';
import { Package, PackageType } from '@products/models/package.model';
import { Product } from '@products/models/product-selection.model';
import { Tariff, TariffPersonalizationType } from '@products/models/tariff.model';
import { includes, isNil, some } from 'lodash';
import { combineLatest, defer, filter, first, map, merge, of, Subject, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ProductBookingLoginGuard {
  readonly #store = inject(Store);

  readonly #changeVisibilityType$ = new Subject<ChangeVisibilityType>();

  readonly #productBookingLoginGuardState = this.setProductBookingLoginGuardStateSignal();

  readonly #isAddTicketsAfterLogin = this.#store.selectSignal(selectIsAddTicketsAfterLogin);
  readonly #checkTariffLimitPerUserAccount = this.#store.selectSignal(selectCheckTariffLimitPerUserAccount);

  get state() {
    return this.#productBookingLoginGuardState;
  }

  showLogin() {
    this.#changeVisibilityType$.next(ChangeVisibilityType.ShowLogin);
  }

  backToShop() {
    this.#changeVisibilityType$.next(ChangeVisibilityType.BackToShop);
  }

  loggedIn() {
    this.#changeVisibilityType$.next(ChangeVisibilityType.LoggedIn);
  }

  isCurrentTariffBookingEnabled(
    tariff: Tariff,
    ticketLimitPerUserAccount: number,
    isVoucherIncludedPerUserAccountLimit = false
  ) {
    const { isVoucher, personalizationType, hasUserAccountTicketLimit } = tariff;

    if (isVoucher) {
      const isAnonymousVoucher = personalizationType === TariffPersonalizationType.Anonymous;

      if (isAnonymousVoucher || !isVoucherIncludedPerUserAccountLimit) {
        return true;
      }
    }

    const tariffHasUserAccountTicketLimit = Boolean(ticketLimitPerUserAccount) || hasUserAccountTicketLimit;

    return this.isCurrentProductBookingEnabled(tariffHasUserAccountTicketLimit);
  }

  isCurrentPackageBookingEnabled(packageType: PackageType, ticketLimitPerUserAccount: number) {
    const packageHasTariffWithUserAccountTicketLimit =
      Boolean(ticketLimitPerUserAccount) ||
      some(packageType?.packages, ({ products }: Package) =>
        some(products, ({ tariffType }: Product) =>
          some(tariffType?.tariffs, ({ hasUserAccountTicketLimit }: Tariff) => hasUserAccountTicketLimit)
        )
      );

    return this.isCurrentProductBookingEnabled(packageHasTariffWithUserAccountTicketLimit);
  }

  private isCurrentProductBookingEnabled(productHasUserAccountTicketLimit: boolean) {
    return this.#checkTariffLimitPerUserAccount()
      ? !this.#isAddTicketsAfterLogin() && !productHasUserAccountTicketLimit
      : true;
  }

  private setProductBookingLoginGuardStateSignal() {
    const hiddenProductSelectionLoginOptions = [
      LoginOptions.BeforeTicketSelection,
      LoginOptions.BeforeTicketSelectionWithRequiredEmailConfirmation
    ];
    const visibleLoginLoginOptions = [
      LoginOptions.OptionalTicketLogin,
      LoginOptions.OptionalTicketAndPersonalLogin,
      ...hiddenProductSelectionLoginOptions
    ];

    const initialState$ = combineLatest({
      exhibitionSettingsLoginMode: this.#store.select(selectExhibitionSettingsLoginMode),
      isUserLoggedIn: this.#store.select(isUserLoggedIn)
    }).pipe(
      filter(({ exhibitionSettingsLoginMode }) => !isNil(exhibitionSettingsLoginMode)),
      map(
        ({ exhibitionSettingsLoginMode, isUserLoggedIn }): ProductBookingLoginGuardState => ({
          isLoginVisible: !isUserLoggedIn && includes(visibleLoginLoginOptions, exhibitionSettingsLoginMode),
          isDisabledProductBookingLogin: false,
          isProductSelectionVisible:
            isUserLoggedIn || !includes(hiddenProductSelectionLoginOptions, exhibitionSettingsLoginMode)
        })
      )
    );

    const showLogin$ = of<ProductBookingLoginGuardState>({
      isLoginVisible: true,
      isDisabledProductBookingLogin: true,
      isProductSelectionVisible: false
    });

    const backToShop$ = defer(() => initialState$.pipe(first()));

    const loggedIn$ = of<ProductBookingLoginGuardState>({
      isLoginVisible: false,
      isDisabledProductBookingLogin: false,
      isProductSelectionVisible: true
    });

    const visibilityTypeStrategies$ = {
      [ChangeVisibilityType.ShowLogin]: showLogin$,
      [ChangeVisibilityType.BackToShop]: backToShop$,
      [ChangeVisibilityType.LoggedIn]: loggedIn$
    };

    const updateState$ = this.#changeVisibilityType$
      .asObservable()
      .pipe(switchMap((changeVisibilityType) => visibilityTypeStrategies$[changeVisibilityType]));

    return toSignal<ProductBookingLoginGuardState>(merge(initialState$, updateState$));
  }
}
